import { Button } from '@mui/material'
import { parseModelUrl } from 'pixai-utils/url'
import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'
import { tv } from 'tailwind-variants'
import { withQuery } from 'ufo'
import IcBaselineChevronRight from '~icons/ic/baseline-chevron-right'
import IcRoundPlusIcon from '~icons/ic/round-plus'
import LoadingSkeletons from 'src/components/Common/LoadingSkeletons'
import type { ModelTypeTagColors } from 'src/components/GenerationModel/modelTypeDisplay'
import { useGenerationModelColorConfig } from 'src/components/GenerationModel/modelTypeDisplay'
import { UIButton } from 'src/components/ui/Button'
import { useMyInfo } from 'src/services/apollo/auth'
import type { GenerationModelType } from 'src/services/apollo/generated/graphql'
import {
  type GenerationModel,
  type GenerationModelVersion,
} from 'src/services/apollo/generated/graphql'
import { fetchListGenerationModelVersions } from 'src/services/apollo/generation-model'
import { cx } from 'src/utils/cx'
import { getNodesFromConnection } from 'src/utils/graphql'
import {
  getModelTypeFromVersion,
  hasModelTypeDisabledAddVersion,
  isLora,
  isUserMultiLora,
} from 'src/utils/model'
import { usePaginatedData } from 'src/utils/pagination'
import { track } from 'src/utils/tracker'
import { dialogAddModelVersions } from '../GenerationModelEdit/ModelEditDialog'
import {
  getModelLoraBaseModelChipConfig,
  getModelVersionStatusChipConfig,
  ModelVersionStatusChip,
} from '../ModelTypeChip'

const SKELETON_SIZES = [68, 74, 62] as const

export default function ModelVersionList(
  props: StyleProps & {
    model: Pick<
      GenerationModel,
      | 'authorId'
      | 'id'
      | 'type'
      | 'latestAvailableVersion'
      | 'isPrivate'
      | 'visibilityType'
    >
    currentVersion?: GenerationModelVersion
  },
) {
  const navigate = useNavigate()
  const { data: userInfo } = useMyInfo()
  const { t } = useTranslation(['market'])
  const colorConfig = useGenerationModelColorConfig()

  const isAuthor = props.model.authorId === userInfo?.id

  const {
    items: versions,
    hasMore,
    loading,
    loadMore,
    reset,
  } = usePaginatedData({
    fetcher: fetchListGenerationModelVersions,
    getArgs: lastData => ({
      modelId: props.model.id,
      first: 20,
      after: lastData?.data?.generationModelVersions?.pageInfo.endCursor,
    }),
    getItems: data =>
      getNodesFromConnection(data.data?.generationModelVersions),
    getHasMore: data =>
      data.data?.generationModelVersions?.pageInfo.hasNextPage ?? false,
  })

  const resetList = useCallback(() => {
    reset()
    loadMore()
  }, [])

  useEffect(() => {
    loadMore()
  }, [])

  const onAddVersion = useCallback(() => {
    track('add_version_click', {
      location: 'model-detail',
      modelType: props.model.type,
    })
    if (isUserMultiLora(props.model.type)) {
      navigate(withQuery('/train-lora', { addToModelId: props.model.id }))
    } else {
      dialogAddModelVersions(props.model, { mutate: resetList })
    }
  }, [props.model])

  if (!hasMore && versions.length === 0 && !isAuthor) {
    return null
  }

  return (
    <div
      className={cx(
        'flex gap-2 py-1 -my-1 overflow-x-auto min-h-[40px]',
        // Paddings for preventing `overflow-auto` from clipping the item outlines.
        'px-[3px] -mx-[3px]',
        props.className,
      )}
      style={props.style}
    >
      <ModelVersionStatusChip
        modelVersion={props.model.latestAvailableVersion}
        className="text-sm text-black rounded-lg whitespace-pre"
      />
      {isAuthor && (
        <UIButton
          icon={<IcRoundPlusIcon className="size-5" />}
          disabled={hasModelTypeDisabledAddVersion(props.model.type)}
          className="flex-none"
          onPress={onAddVersion}
        >
          {t('market:submit.add-version')}
        </UIButton>
      )}

      {versions.map(v => (
        <ListItem
          version={v}
          key={v.id}
          modelType={props.model.type}
          current={v.id === props.currentVersion?.id}
          className="flex-none"
          colorConfig={colorConfig}
        />
      ))}
      {hasMore &&
        (versions.length === 0 ? (
          <LoadingSkeletons amount={SKELETON_SIZES.length}>
            {idx => (
              <span
                className="block h-8 bg-black/20 dark:bg-white/20 rounded-md animate-pulse"
                style={{ width: SKELETON_SIZES[idx] }}
              />
            )}
          </LoadingSkeletons>
        ) : (
          <UIButton
            icon={<IcBaselineChevronRight className="size-6" />}
            busy={loading}
            onPress={() => void loadMore()}
          />
        ))}
    </div>
  )
}

const itemStyle = tv({
  base: 'flex items-center h-8 px-2 py-1 bg-black/5 dark:bg-white/5',
  variants: {
    current: {
      true: 'outline outline-2 outline-offset-1 outline-purple-600',
      false: 'hover:bg-black/10 dark:hover:bg-white/10',
    },
  },
})

function ListItem(
  props: StyleProps & {
    version: GenerationModelVersion
    modelType: GenerationModelType
    current?: boolean
    colorConfig?: ModelTypeTagColors
  },
) {
  const { version, modelType } = props

  const { t } = useTranslation(['market'])

  const modelVersionStatusChipProp = getModelVersionStatusChipConfig(t, version)

  const loraBaseChipProps = getModelLoraBaseModelChipConfig(
    getModelTypeFromVersion(version),
    props.colorConfig,
  )

  const getTargetUrl = () => {
    const modelUrl = parseModelUrl(location.pathname)
    const currentVersionId = modelUrl?.versionId
    const modelPath = `/model/${modelUrl?.modelId}`
    if (version.id === currentVersionId && modelUrl) {
      return modelPath
    } else {
      return `${modelPath}/${version.id}`
    }
  }

  return (
    <Button
      component={Link}
      to={getTargetUrl()}
      data-current={props.current ? '' : null}
      className={itemStyle({
        current: props.current,
        className: ['group', props.className],
      })}
      style={props.style}
    >
      <span className="leading-5 font-semibold">
        {version.name}
        {modelVersionStatusChipProp && (
          <span
            className={cx(
              'ml-1 px-1.5 text-xs text-black/60 rounded-full',
              modelVersionStatusChipProp.bgClassName,
            )}
          >
            {modelVersionStatusChipProp.label}
          </span>
        )}
        {/* Always display base model type for Loras */}
        {isLora(modelType) && loraBaseChipProps && (
          <span
            className={cx(
              'ml-1 px-1.5 text-xs text-black/60 rounded-full',
              loraBaseChipProps.bgClassName,
            )}
            style={loraBaseChipProps.bgStyle}
          >
            {loraBaseChipProps.label}
          </span>
        )}
      </span>
    </Button>
  )
}
